import { inject, Injectable, Signal, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { timer, Subscription } from 'rxjs';
import { MatchProtoService } from '../../services';
import { Buffer } from 'buffer';
import { TranslationService } from '@/app/services/translation.service';

export enum EEvent {
  STATS = 'stats',
  INCIDENTS = 'incidents',
  MATCHES = 'matches',
}

// export enum EEvent {
//   STATS = 0,
//   INCIDENTS = 1,
//   MATCHES = 2,
// }

type SocketContext = {
  socket$: WebSocketSubject<any> | null;
  isConnected: Signal<boolean>;
  data: Signal<any>;
  connect: () => void;
  disconnect: () => void;
};

@Injectable({ providedIn: 'root' })
export class WsMatchManagerService {
  private proto = inject(MatchProtoService);
  private translationService = inject(TranslationService);

  private sockets = new Map<EEvent, SocketContext>();

  viewLogs = false;

  constructor() {
    this.initSocket(EEvent.STATS);
    this.initSocket(EEvent.INCIDENTS);
    this.initSocket(EEvent.MATCHES);
  }

  private initSocket(event: EEvent): void {
    const connected = signal(false);
    const data = signal<any>(null);
    let socket$: WebSocketSubject<any> | null = null;

    let reconnectSubscription: Subscription | null = null;
    let manualDisconnect = false;

    const connect = () => {
      if (socket$ && !socket$.closed) return;

      manualDisconnect = false;

      const wsUrl = `${
        environment.wsMatchtUrl
      }/ws?event=${event}&language=${this.handleLangFromProto()}`;

      this.proto.loadProto().then((root) => {
        const GenericResponse = root.lookupType(
          'client_events.GenericResponse'
        );

        socket$ = webSocket({
          url: wsUrl,
          binaryType: 'blob',
          deserializer: ({ data }) => data,
          openObserver: {
            next: () => {
              connected.set(true);
              if (this.viewLogs) {
                console.log('[WS] ✅ Conectado');
              }
              // const pingMsg = { ping: { reason: 'initial' } };
              // const encoded = this.proto.encodeRequest(pingMsg);
              // socket$?.next(encoded);
            },
          },
          closeObserver: {
            next: () => {
              connected.set(false);
              if (this.viewLogs) {
                console.warn('[WS] 🔴 Desconectado');
              }
              socket$ = null;

              if (!manualDisconnect) {
                if (this.viewLogs) {
                  console.log(
                    `[WS] 🔄 Reintentando conexión para ${event} en 5s...`
                  );
                }
                reconnectSubscription?.unsubscribe();
                reconnectSubscription = timer(5000).subscribe(() => connect());
              }
            },
          },
        });

        socket$.subscribe({
          next: async (rawData) => {
            try {
              // Validar que hay data
              if (!rawData || !(rawData instanceof Blob)) {
                if (this.viewLogs) {
                  console.warn(
                    `[WS] ⚠️ Datos vacíos o formato inesperado (${event})`,
                    rawData
                  );
                }
                return;
              }

              const buffer = await rawData.arrayBuffer();
              if (!buffer || buffer.byteLength < 4) {
                if (this.viewLogs) {
                  console.warn(
                    '[WS] ⚠️ Buffer inválido o muy corto. Ignorado.'
                  );
                }
                return;
              }

              // Si el buffer está vacío, no proceses
              if (buffer.byteLength === 0) {
                if (this.viewLogs) {
                  console.warn(`[WS] ⚠️ Blob vacío recibido en ${event}`);
                }
                return;
              }

              const byteBuffer = Buffer.from(new Uint8Array(buffer));
              const decoded = GenericResponse.decode(byteBuffer);
              const decodedObject = GenericResponse.toObject(decoded, {
                defaults: true,
              });

              if (this.viewLogs) {
                console.log(
                  `[WS] 🟢 Mensaje decodificado (${event}):`,
                  decodedObject
                );
              }
              if (decodedObject) {
                data.set(decodedObject);
              } else {
                data.set({});
              }
            } catch (err) {
              if (this.viewLogs) {
                console.error(`[WS ❌ ${event}] Error al decodificar:`, err);
              }
              // Podrías contar errores, pero no desconectar aún si es el primero
            }
          },
          error: (err) => {
            if (this.viewLogs) {
              console.error(`[WS 🔥 ${event}] error`, err);
            }
          },
          complete: () => connected.set(false),
        });
      });
    };

    const disconnect = () => {
      manualDisconnect = true;
      reconnectSubscription?.unsubscribe();
      reconnectSubscription = null;
      socket$?.complete();
      socket$ = null;
      connected.set(false);
    };

    this.sockets.set(event, {
      socket$,
      isConnected: connected.asReadonly(),
      data: data.asReadonly(),
      connect,
      disconnect,
    });
  }

  public getChannel(event: EEvent): SocketContext {
    const context = this.sockets.get(event);
    if (!context)
      throw new Error(`WebSocket context for ${event} not initialized`);
    return context;
  }

  private handleLangFromProto(): string {
    const lang = this.translationService.getLangLocalStorage();
    return lang === 'en' ? 'english' : 'spanish';
  }
}
